import {MavenTemplateEntry, MavenTemplateIndicators, MavenTemplateSettings, MavenTemplateDates} from '../templates/maven-settings-template';
import {captureMavenTemplate, captureMavenIndicators, defaultMavenTemplateDates, resolveMavenTemplateDates, restoreMavenSide, expandMavenIndicators} from '../templates/maven-template-mapper';
import {diffMavenTemplate, mergeMavenChanges} from '../templates/maven-template-diff';
import {assertMavenTemplateSettings, validateMavenTemplateSettings} from '../templates/maven-template-validation';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren
} from '@angular/core';
import {Subject} from 'rxjs';
import {filter, takeUntil} from 'rxjs/operators';
import {ToastrService} from 'ngx-toastr';
import {ApgRollPositionsPanelComponent} from '../../adjustment-pricing-grid/copilot/apg-roll-positions-panel.component';
import {DetectMethodChanges} from '../../utils';
import {ApgPortfolio} from '../../adjustment-pricing-grid/model/ApgPortfolio';
import {
    buildPortfolioGroups,
    PortfolioGroup
} from '../../adjustment-pricing-grid/services/portfolio-groups';
import {withDefaultTemplate} from './maven-template-list';
import {ApgDataService} from '../../adjustment-pricing-grid/services/apg-data.service';
import {CashFlowPortfoliosService} from '../../adjustment-pricing-grid/services/cashflow-portfolios.service';
import {
    CashFlowStrategyTemplatesService,
    LastUsedTemplate
} from '../../adjustment-pricing-grid/services/cashflow-strategy-templates.service';
import {ShellTodayService} from '../../shell-clock/shell-today.service';
import {
    ICashFlowAdjustmentSettingsTemplate
} from '../../adjustment-pricing-grid/model/ICashFlowAdjustmentSettingsTemplate';
import {SessionService} from '../../authentication/session-service.service';
import {OrionSettingsService} from '../../orion-settings-dialog/orion-settings.service';
import {ExpectedMoveService} from '../../expected-move/expected-move.service';
import {OptionsChainService} from '../../option-chains.service';
import {TradingInstrumentsService} from '../../trading-instruments/trading-instruments-service.interface';
import {TradingInstrument} from '../../trading-instruments/trading-instrument.class';
import {OptionExpirationDescriptor} from '../../shell-communication/shell-dto-protocol';
import {
    emptyMavenSetup,
    MavenCloseSelection,
    MavenExistingHedges,
    MavenRun,
    MavenSetupInputs
} from '../maven-domain';
import {MavenCloseBookView} from './maven-close-selection.component';
import {
    canApply,
    MavenSetupLimits,
    MavenSetupProblem,
    MavenSetupSection,
    noLimits,
    problemsAt,
    validateMavenSetup
} from './maven-setup-validation';
import {IMavenRunSkill, MAVEN_RUN_SKILL} from '../run-client/maven-run-skill';
import {MavenRefusalPlacement, resolveMavenRefusal} from '../run-client/maven-refusal-messages';
import {MavenCardDto, WarmMavenSlots} from '../run-client/maven-operations';
import {MAVEN_CONTRACT_VERSION} from '../run-client/maven-operations';
import {toCalendarDateString} from '../run-client/maven-run-client';
import {RollPositionsInputs, Weekday} from '../../adjustment-pricing-grid/copilot/apg-copilot-domain';
import {dayKey, survivingDates} from '../../adjustment-pricing-grid/copilot/apg-copilot-questionnaire';
import {MarketConditionOverrides, OVERRIDE_VALUE_KEYS} from '../../copilot-shared/opg-copilot-domain';
import {ShellClientService} from '../../shell-communication/shell-client.service';
import {MessageBusService} from '../../message-bus.service';
import {AppSettingsUpdatedMessageTopic} from '../../app-settings/application-settings.service';
import {UserSettingObject, UserSettingsService} from '../../user-settings.service';
import {HedgePositionsService} from '../../hedging-grid/positions-section/hedge-positions/hedge-positions.service';
import {
    applyRunDataEcho,
    makeRunDataBaseline,
    MavenRunDataBaseline,
    MavenRunDataChanged,
    MavenRunDataKind,
    runDataChangedSentence
} from './maven-run-data-watch';
import {isValidNumber, isVoid, makePortfolioKey} from '../../utils';
import {BeforePositionDto} from '../../adjustment-pricing-grid/model/BeforePositionDto';
import {
    formatPortfolioZone,
    hasPortfolioZone,
    isPortfolioZoneEcho,
    MavenPortfolioZone,
    NO_PORTFOLIO_ZONE,
    readPortfolioZone
} from './maven-portfolio-zone';
import {
    GetPortfolioCurrentZoneData,
    GetPortfolioCurrentZoneDataReply
} from '../../current-zone-indicator/model/cqrs.class';
import {CurrentZoneChangedMessage} from '../../current-zone-indicator/popover/zone-levels-popover.component';
import {MavenSideParametersComponent} from './maven-side-parameters.component';
import {
    biasChips,
    closeSelectionChips,
    datesChips,
    existingHedgesChips,
    hedgeParametersChips,
    MavenChip,
    MavenSideCrumb,
    overridesChips,
    portfolioChips
} from './maven-setup-breadcrumbs';
import {
    anyKeyWithPrefix,
    cloneMavenSetup,
    diffMavenSetup,
    MavenParamChangeMap
} from './maven-setup-param-diff';

// The Setup's presentation sections: the four the validator knows, plus the two that gate
// nothing — Directional Bias (inert, ruling L47) and, since CR-20260915-001, Override Market
// Indicators (its completeness rule lives on the map, not on the section).
type MavenSetupPanelSection = MavenSetupSection | 'bias' | 'overrides' | 'settingsTemplate';

interface MavenDateSelectorOverrides {
    dateKeys: string[];
    overrides: MarketConditionOverrides;
    allDates: MavenTemplateIndicators | null;
}

// THE SETUP (CR-20260910-001 milestone 4, feature request section 4.1, S1-S11).
//
// The Kanban THE SETUP column is the definition of this column, and the April 2025 sketch is
// SUPERSEDED for it: the sketch's zone HEADER is dropped and there is NO OVERRIDE ZONE (ruling
// Q4). ⚠ Q4's other half — "the footer zone indicator shows the zone, so the Setup does not" —
// was REPLACED by the owner on 2026-09-14 (vrequest): the portfolio's CURRENT ZONE is shown as a
// read-only line under the Adjustment template, N/A blocks Apply, and a zone that moved since the
// last run is marked stale. Read the same way the APG reads it: GetPortfolioCurrentZoneData with
// the COMPOSITE portfolio key (the bare id was the G1 "no current zone" defect), re-read on the
// footer's CurrentZoneChanged.
//
// PRESENTATION: vertical collapsible sections, never a wizard — the owner does not want
// information hidden behind paging. The section being worked on carries a distinct border.
// Sections are a HINT, not a lock: a locked section hides the reason it is locked, while Apply
// already refuses an incomplete hierarchy and says which control is at fault.
//
// THE PANEL SENDS IDS ONLY (A5): portfolio id, portfolio OWNER id, template id. The saved
// positions, the saved hedge book, the template body and the expected-range model are read on the
// BACKEND, for the owner — which is why the owner id rides on the order at all: a super user may
// run on another trader's portfolio, and every saved-state read must follow the portfolio, not
// the login.
//
// The working Setup is session-only. Explicit Maven settings templates capture a separate reusable
// subset; APG's per-portfolio adjustment-template selection is still read-only to Maven.
//
// NO STOP BUTTON (R-D): Apply replaces the run in flight. There is no cancel command to call.
@Component({
    selector: 'ets-maven-setup',
    templateUrl: './maven-setup.component.html',
    styleUrls: ['./maven-setup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MavenSetupComponent implements OnInit, OnDestroy {

    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _shellClient: ShellClientService,
        private readonly _messageBus: MessageBusService,
        private readonly _sessionService: SessionService,
        private readonly _portfoliosService: CashFlowPortfoliosService,
        private readonly _templatesService: CashFlowStrategyTemplatesService,
        private readonly _apgDataService: ApgDataService,
        private readonly _orionSettings: OrionSettingsService,
        private readonly _expectedMove: ExpectedMoveService,
        private readonly _optionChains: OptionsChainService,
        private readonly _tradingInstruments: TradingInstrumentsService,
        // CR-20260916-001: the expired-legs refusals are shown as a warning toast (owner ruling 4).
        private readonly _toastr: ToastrService,
        // CR-20260916-004: the run-data watch — the settings echo, and the two services that know
        // which keys the positions and the hedge book live under (for the portfolio OWNER).
        private readonly _userSettings: UserSettingsService,
        private readonly _hedgePositions: HedgePositionsService,
        // CR-20260916-005: the one "what is today" — the QG replay cursor or the server time,
        // never the browser clock. Clamps the Start anchor and re-seeds when the day flips.
        private readonly _shellToday: ShellTodayService,
        @Inject(MAVEN_RUN_SKILL) private readonly _runSkill: IMavenRunSkill
    ) {
    }

    templateUnderlying: string | null = null;
    templateBusy = false;
    private _selectionGeneration = 0;
    private _allDatesOverride: MavenTemplateIndicators | null = null;
    private _overrideSelectorKey: string | null = null;
    private _overrideStates: {[dateSelector: string]: MavenDateSelectorOverrides} = {};
    private _templateDates: MavenTemplateDates = defaultMavenTemplateDates();
    private _selectedTemplateSettings: MavenTemplateSettings | null = null;
    private _templateChangedKeys: MavenParamChangeMap = {};
    private reusableSettings(): MavenTemplateSettings {
        return captureMavenTemplate(this.setup, this._anchorKey, this._allDatesOverride, this._templateDates);
    }
    readonly captureSettingsTemplate = (): MavenTemplateSettings => {
        const settings = this.reusableSettings();
        assertMavenTemplateSettings(settings);
        return settings;
    };
    get templateSaveProblems(): string[] {
        try { return validateMavenTemplateSettings(this.reusableSettings()); }
        catch (e) { return [e.message || 'Template Settings Are Incomplete.']; }
    }

    @DetectMethodChanges()
    onTemplateBusy(busy: boolean): void { this.templateBusy = busy; }

    @DetectMethodChanges()
    onAllDatesOverride(value: MavenTemplateIndicators | null): void {
        this._allDatesOverride = captureMavenIndicators(value);
        // Explicit All Dates edits/clear supersede automatically remembered values. This event
        // is emitted only by a human edit/clear, never by date changes or template application.
        Object.keys(this._overrideStates).forEach(key => {
            const state = this._overrideStates[key];
            state.allDates = captureMavenIndicators(value);
            state.overrides = expandMavenIndicators(state.allDates, state.dateKeys);
        });
        this.refreshChangedKeys();
    }

    @DetectMethodChanges()
    onTemplateBaselineChanged(entry: MavenTemplateEntry | null): void {
        // A selection/save/deletion establishes a new template scope. Keep the current working
        // values, but do not restore remembered values belonging to the previous baseline.
        this._overrideStates = {};
        this._selectedTemplateSettings = entry && entry.template.underlying === this.underlying
            ? JSON.parse(JSON.stringify(entry.template.settings)) : null;
        if (this._selectedTemplateSettings) {
            this._templateDates = this._selectedTemplateSettings.dates;
        }
        this.refreshChangedKeys();
    }

    get templateDifferent(): boolean {
        return anyKeyWithPrefix(this._templateChangedKeys, ['']);
    }

    @DetectMethodChanges()
    onSettingsTemplateSelected(entry: MavenTemplateEntry): void {
        if (!entry || entry.template.underlying !== this.underlying || this.isRunning) { return; }
        this.clearOverrideHistory();
        const settings = entry.template.settings;
        this._templateDates = settings.dates;
        this.setup.existingHedges = settings.existingHedges;
        this.setup.closeSelection = {expirations: [], hedgeIds: []};
        this.setup.calls = restoreMavenSide(settings.calls);
        this.setup.puts = restoreMavenSide(settings.puts);
        const dates = resolveMavenTemplateDates(settings.dates, this._anchorKey,
            this.availableExpirations, this.maxRangeDays);
        this.restoreTemplateDates(dates);
        this._allDatesOverride = captureMavenIndicators(settings.allDatesOverride);
        this.setup.marketOverrides = expandMavenIndicators(this._allDatesOverride, this._overrideDateKeys);
        this.backendProblem = null;
        this.refreshChangedKeys();
    }

    private restoreTemplateDates(dates: RollPositionsInputs): void {
        if (this._datesQuestionnaire) { this._datesQuestionnaire.restoreInputs(dates); }
        else { this.onDatesChanged({inputs: dates, valid: false}); }
    }

    @DetectMethodChanges()
    private finishTemplateFamilyLoad(): void {
        this._templateDates = defaultMavenTemplateDates();
        this.restoreTemplateDates(resolveMavenTemplateDates(this._templateDates, this._anchorKey,
            this.availableExpirations, this.maxRangeDays));
        this.templateUnderlying = this.underlying;
    }

    // Supersede scope, with the terminal. Supplied by the panel, which owns the instance id.
    @Input() panelInstanceId: string;

    // THE REUSED DATE QUESTIONNAIRE OWNS ITS OWN STATE. Maven only hears it through stateChange, so
    // replacing `setup.dates` on Reset changes nothing the trader can see — the pickers kept their
    // dates (owner smoke, 2026-09-13). Reset has to tell the child, through its own public reset.
    // The child is KEPT ALIVE while the Date Range section is collapsed (only hidden — owner
    // vrequest 2026-09-15: a fold must not lose the dates), so this is defined once the view is,
    // open or collapsed, and Reset reaches it either way.
    @ViewChild(ApgRollPositionsPanelComponent) private _datesQuestionnaire: ApgRollPositionsPanelComponent | undefined;

    // ---- OUTPUTS — the seam to the other two columns (CR-20260910-001, the G3 wiring defect) ----
    //
    // ⚠ WHY THESE EXIST. This component held `isRunning`, `currentRun` and `cards` in its own
    // fields and emitted NOTHING, while the panel template placed <ets-maven-results> with no
    // bindings at all. Each half was correct in isolation, nothing joined them, and so a run that
    // delivered five cards rendered an empty column. Every gate passed because no spec rendered the
    // PANEL with a delivery — which is exactly how it reached the owner.
    //
    // The fields stay: they drive this column's own Apply button and refusal placement. These are
    // additive, and they are the only route by which a result leaves this component.

    /** True from Apply until the run settles. Drives the Results column's stage rail. */
    @Output() runningChange = new EventEmitter<boolean>();

    /** The stage CONSTANTS raised so far, in arrival order. The column maps them to labels. */
    @Output() stagesChange = new EventEmitter<string[]>();

    /**
     * The settled run. Emitted ONCE, and never for a displaced run — the supersede arm returns
     * without calling `endRun`, so a late result cannot blank the column that replaced it.
     */
    @Output() runSettled = new EventEmitter<MavenRunSettled>();

    /** The selected portfolio — the Details column needs it to open either order pop-up. */
    @Output() portfolioChange = new EventEmitter<ApgPortfolio | null>();

    // `_orionSettings` is a constructor parameter property, assigned before field initialisers run.
    setup: MavenSetupInputs = emptyMavenSetup(freshDates(this._orionSettings.mavenWeekdayExclusions));

    // GROUPED, not flat. dx-select-box consumes this with [grouped]="true": `key` is the header
    // text naming the owner. A flat list is what made two <No Portfolio> rows — two DIFFERENT
    // traders' default portfolios — indistinguishable on Alan's screen.
    portfolioGroups: PortfolioGroup[] = [];

    templates: ICashFlowAdjustmentSettingsTemplate[] = [];

    selectedPortfolio: ApgPortfolio | null = null;
    get presetApgSource(): string {
        return JSON.stringify({ownerId: this.selectedPortfolio && this.selectedPortfolio.userId,
            portfolioId: this.selectedPortfolio && this.selectedPortfolio.id, templateId: this.setup.templateId});
    }

    // The portfolio's default order quantity. Used HERE and only here, to convert a normalized
    // money entry to plain dollars before it crosses. It does not itself cross.
    defaultQty: number | null = null;

    // Whether Expected Range can be OFFERED at all — the ORION (OPG)'s own rule, asked of the
    // same service so the two panels cannot drift: the setting must be enabled AND its model must
    // resolve to a band. Where it is not usable the pill is DISABLED IN PLACE, never removed.
    //
    // ⚠ ONE HONEST LIMIT. This reads the LOGGED-IN user's setting, because that is the only one
    // the frontend has. The backend judges the PORTFOLIO OWNER's setting (ruling R-E) and refuses
    // ExpectedRangeUnavailable on that basis. The two can disagree for exactly one case — a super
    // user running on another trader's portfolio — and when they do, the BACKEND is right and its
    // refusal is surfaced beside this very control. Reading the owner's setting here would mean
    // shipping the expected-range model to the frontend, which contract M1 section 8 keeps off
    // the wire on purpose.
    expectedRangeAvailable = true;

    // ⚠ THE SAVED-HEDGE READ-OUT WAS REMOVED HERE (G3, Alan): "he does not want the hedges shown,
    // only the choice." Existing Hedges is two buttons and nothing else. The book is still READ on
    // the backend, by portfolio id, for the owner — nothing about the run changed, only what this
    // column displays. Do not reinstate the state, the fetch or the leg formatter.

    // FED TO THE END-STRIKE PICKER on both sides. Without them the platform strike dropdown
    // self-disables, and a Range coverage becomes impossible to complete — the control renders and
    // does nothing. They are the portfolio's underlying instrument and the NEAREST listed
    // expiration, which is DATE-INDEPENDENT of the range the trader chose above: the ladder of
    // available strikes is what is being picked from, not a particular day's chain. That is the
    // ORION (OPG)'s own rule for its pickers, kept here so the two panels offer the same list.
    targetInstrument: TradingInstrument | null = null;

    strikeExpiration: OptionExpirationDescriptor | null = null;

    // ---- The Current Zone line (owner vrequest 2026-09-14) -------------------------------------
    //
    // The selected portfolio's current zone, read from GetPortfolioCurrentZoneData with the
    // COMPOSITE key on every portfolio selection and re-read when the footer indicator publishes
    // CurrentZoneChanged for that same portfolio. null = N/A — no portfolio, no zone picked for
    // it yet, the read in flight, or the read failed. It NEVER keeps the previous portfolio's
    // number: a stale zone from another portfolio would be worse than no zone at all.
    // Session only, like everything else here; and read-only — there is no Override Zone.
    currentZone: number | null = null;

    // The zone the LAST RUN was sent with, or null before the first Apply (and after Reset, and
    // after a portfolio change — the comparison means nothing across portfolios). When the current
    // zone differs from it, the number gets the family's yellow stale border: the cards on the
    // right were priced for a zone that is no longer this one (owner ruling, 2026-09-14).
    lastRunZone: number | null = null;

    // ---- The Portfolio Zone (owner vrequest 2026-09-18) ----------------------------------------
    //
    // The strike of the portfolio's short options — the near-dated ShortOption leg per side, read
    // from the saved APG positions under the SAME key the run-data watch snapshots, for the
    // portfolio OWNER. Sits on the zone line to the LEFT of the market zone (owner screencast
    // 2026-09-21: starting point first, ending point second): "Portfolio Zone 6930 | 6910 Market
    // Zone". Both strikes when the sides differ; N/A when no portfolio, no saved
    // positions, no short leg, or an expired one. Re-read from the settings ECHO (never a
    // re-read: for another trader's portfolio the services hold the login-time copy). Display
    // only — the run still sends ids and the backend reads the positions itself.
    portfolioZone: MavenPortfolioZone = NO_PORTFOLIO_ZONE;

    // ---- The changed-since-run cues (owner vrequest 2026-09-15) --------------------------------
    //
    // THE SETUP THE LAST RUN WAS SENT WITH — a deep copy taken at Apply, or null before the first
    // Apply, after Reset, and after a portfolio change (owner ruling 2026-09-15: a portfolio change
    // has the same effect as Reset). Every ORION marks a field yellow when its live value no longer
    // matches what the results on screen were produced with; Maven does the same, on every level:
    // the field, the Calls / Puts side, the section header, and the collapsed cradle's chip.
    //
    // Apply is DISABLED while a run is in flight (canRun), so there is exactly one baseline per
    // run and it is never replaced mid-flight.
    lastRunSetup: MavenSetupInputs | null = null;

    // The live Setup diffed against that baseline: a flat map of changed-field keys (grammar in
    // maven-setup-param-diff.ts). Recomputed after every edit, never in a getter — it is fed to
    // three OnPush children as an input, and a FRESH object per recompute is what wakes them.
    // Since 2026-10-06 this map unites template and last-run differences; each baseline remains
    // independent so running cannot erase differences from the selected settings template.
    // Null = clean, every cue off.
    private _changedKeys: MavenParamChangeMap | null = null;

    get changedKeys(): MavenParamChangeMap | null {
        return this._changedKeys;
    }

    private refreshChangedKeys(): void {
        // Money is compared as money, on the CURRENT default quantity (owner ruling 2026-09-15).
        const settings = this._selectedTemplateSettings;
        this._templateChangedKeys = diffMavenTemplate(settings, this.setup,
            settings ? resolveMavenTemplateDates(settings.dates, this._anchorKey,
                this.availableExpirations, this.maxRangeDays) : null,
            this._allDatesOverride, this._overrideDateKeys || [], this.defaultQty);
        const map = mergeMavenChanges(diffMavenSetup(this.lastRunSetup, this.setup, this.defaultQty),
            this._templateChangedKeys);
        this._changedKeys = anyKeyWithPrefix(map, ['']) ? map : null;
    }

    // (A portfolio change clears this baseline by running the full Reset first — see
    // selectPortfolio — so there is no separate "clear on portfolio change" here.)

    // ---- The run-data watch (owner vrequest 2026-09-16, CR-20260916-004) -----------------------
    //
    // The cues above cover the Setup FIELDS. The saved positions and the saved hedge book the run
    // was priced on are watched separately, through the shell's user-settings echo, against a
    // per-key JSON snapshot taken at Apply (maven-run-data-watch.ts explains why the echoed value,
    // never a re-read). The verdict is ONE pinned stripe under Expand all / Collapse all:
    // "Portfolio Positions Changed" / "Hedges Changed" / "Portfolio Positions and Hedges Changed".
    // Owner rulings 2026-09-16: baseline read on the frontend at Apply (A); one line for both;
    // the hedge line shows under Ignore too (a later Close would use the book).
    // Null baseline = no run (never applied, Reset, portfolio change) = no stripe.
    private _runData: MavenRunDataBaseline | null = null;
    private _runDataLive: { [key: string]: boolean } = {};
    runDataChanged: MavenRunDataChanged | null = null;

    // The Current Zone rides on the same stripe (owner, 2026-09-16, first build): zoneStale is the
    // existing "moved since the run" fact (manual set from the footer, or a re-read). The number's
    // own yellow border stays as it was — two cues, one fact.
    get runDataSentence(): string | null {
        return runDataChangedSentence(this.runDataChanged, this.zoneStale);
    }

    // At Apply, BEFORE the wire: the values under the watched keys, read for the portfolio owner
    // the same way the services read them. A read that fails leaves no baseline — no stripe is
    // better than a wrong one — and the run still goes out.
    private async snapshotRunData(portfolio: ApgPortfolio): Promise<void> {
        this._runData = null;
        this._runDataLive = {};
        this.runDataChanged = null;
        try {
            const entries: { key: string; kind: MavenRunDataKind; value: any }[] = [];
            const positionsKey = await this._apgDataService.getPortfolioPositionsKey(portfolio);
            if (positionsKey) {
                entries.push({
                    key: positionsKey,
                    kind: 'positions',
                    value: this._userSettings.getValue(positionsKey, portfolio.userId)
                });
            }
            const hedgeKeys = await this._hedgePositions.getHedgePositionsKeys(portfolio);
            hedgeKeys.forEach(key => entries.push({
                key,
                kind: 'hedges',
                value: this._userSettings.getValue(key, portfolio.userId)
            }));
            this._runData = makeRunDataBaseline(portfolio.userId, entries);
        } catch (e) {
            console.error('Maven: run-data baseline not taken; the changed-data stripe is off for this run', e);
        }
    }

    @DetectMethodChanges()
    private onUserSettingEcho(echo: UserSettingObject): void {
        // The Portfolio Zone follows the saved positions of the SELECTED portfolio, run or no
        // run. ⚠ Not "echo.key === the key I resolved": the key carries the LAST-USED TEMPLATE's
        // underlying and strategy, so a template switch in the APG moves the positions to a new
        // key and re-saves them there. Any positions echo for this portfolio, and any templates
        // echo for its owner, re-resolves the key and re-reads (owner, 2026-09-18: "if portfolio
        // positions change, the value must be updated").
        const owner = this.selectedPortfolio;
        if (owner && echo.userId === owner.userId && isPortfolioZoneEcho(echo.key, owner.id)) {
            this.refreshPortfolioZone(owner, echo);
        }
        const changed = applyRunDataEcho(this._runData, this._runDataLive, echo);
        if (changed === null) {
            return;   // not one of this run's keys — nothing to repaint
        }
        this.runDataChanged = (changed.positions || changed.hedges) ? changed : null;
    }

    // Template guard for one control's [class.param-changed].
    isChanged(key: string): boolean {
        return !!(this._changedKeys && this._changedKeys[key]);
    }

    // The section header's cue: anything under the section changed. The prefixes are the diff's
    // key grammar, grouped the way the Setup is laid out — Directional Bias lives on the date
    // questionnaire's inputs but is its own section here, so it gets its own row.
    private static readonly SECTION_PREFIXES: { [section: string]: string[] } = {
        portfolio: ['portfolio', 'template'],
        dates: ['date.', 'exclusions.'],
        existingHedges: ['existingHedges'],
        hedgeParameters: ['calls.', 'puts.'],
        overrides: ['ovr.'],
        bias: ['bias.']
    };

    sectionChanged(section: MavenSetupPanelSection): boolean {
        return anyKeyWithPrefix(this._changedKeys, MavenSetupComponent.SECTION_PREFIXES[section] || []);
    }

    // A cradle chip's cue: the chip carries the prefix(es) of what it summarises.
    chipChanged(c: { key: string | null; keys?: string[] }): boolean {
        const prefixes = c.keys ? c.keys : (c.key ? [c.key] : []);
        return prefixes.length > 0 && anyKeyWithPrefix(this._changedKeys, prefixes);
    }

    private readonly _unsubscriber = new Subject<void>();

    isRunning = false;

    currentRun: MavenRun | null = null;

    cards: MavenCardDto[] = [];

    /**
     * "single" | "double" from the last SUCCEEDED run, or null.
     *
     * Held exactly as `defaultQty` is, and for the same reason: it is a run-level portfolio fact the
     * cards are meaningless without, and both order pop-ups need it (ruling L39). Null until a run
     * succeeds, and NEVER defaulted — an absent strategy type leaves the order controls disabled
     * rather than becoming a guessed one, because both ways of getting it wrong are silent.
     */
    strategyType: string | null = null;

    // A refusal that came back from the BACKEND, rendered beside the same control a local refusal
    // would use. The trader cannot tell the two apart, and should not have to.
    backendProblem: { placement: MavenRefusalPlacement; message: string; isDefect: boolean } | null = null;

    // THE SECTIONS ARE MAVEN'S OWN COLLAPSIBLES (vrequest 2026-09-13; the DevExtreme accordion was
    // tried and ruled out by the owner the same day). All open at start: nothing is hidden behind
    // a click the trader did not make. Any combination may be open.
    // 'bias' is a presentation section only — it is not a validation section, because the bias
    // gates nothing (ruling L47).
    private readonly _open: { [section: string]: boolean } = {
        portfolio: true,
        settingsTemplate: true,
        dates: true,
        existingHedges: true,
        hedgeParameters: true,
        overrides: true,
        bias: true
    };

    isOpen(section: MavenSetupPanelSection): boolean {
        return this._open[section] !== false;
    }

    @DetectMethodChanges()
    toggleSection(section: MavenSetupPanelSection): void {
        this._open[section] = !this.isOpen(section);
    }

    // ---- The breadcrumb cradles (owner vrequest 2026-09-15) -------------------------------------
    //
    // Rendered under a COLLAPSED section's header only: a strip of chips saying what the section
    // holds, so nothing is hidden behind a fold. Pure projections of the live Setup — the
    // formatters in maven-setup-breadcrumbs.ts own the wording. Clicking the strip re-opens the
    // section, as the ORIONs' strips do. The sides fold inside an open Hedge Parameters and carry
    // their own cradle in their own component.
    get portfolioCrumbs(): MavenChip[] {
        const p = this.selectedPortfolio;
        const template = this.setup.templateId
            ? this.templates.filter(t => t.templateId === this.setup.templateId)[0]
            : null;
        return portfolioChips({
            portfolioLabel: p ? p.userName + '  |  ' + p.name : null,
            templateName: template ? template.templateName : null,
            zone: this.currentZone
        });
    }

    get datesCrumbs(): MavenChip[] {
        return datesChips(this.setup.dates);
    }

    // Under Close (CR-20260916-003) the plain chip is followed by the per-side selection chips,
    // built from the list the child reported (the Setup holds ids only; the names are the child's).
    get existingHedgesCrumbs(): MavenSideCrumb[] {
        const base: MavenSideCrumb[] = existingHedgesChips(this.setup.existingHedges)
            .map(c => ({side: null, key: c.key, keys: c.key ? [c.key] : [], text: c.text}));
        if (this.setup.existingHedges !== 'close' || !this.closeBook) {
            return base;
        }
        return base.concat(closeSelectionChips(this.closeBook.boxes));
    }

    // ---- CR-20260916-003, the Close selection ---------------------------------------------

    // What the child list reported: which hedges each expiration holds, the labels, the per-box
    // summary. Null under Ignore and before the list is built. Feeds the validator (limits) and
    // the cradle chips.
    closeBook: MavenCloseBookView | null = null;

    @DetectMethodChanges()
    onCloseSelectionChange(next: MavenCloseSelection): void {
        this.setup.closeSelection = next;
        this.backendProblem = null;
        this.refreshChangedKeys();
    }

    @DetectMethodChanges()
    onCloseBookChange(view: MavenCloseBookView): void {
        this.closeBook = view;
    }

    // One big chip per side, tinted by side (owner, 2026-09-15).
    get hedgeParametersCrumbs(): MavenSideCrumb[] {
        return hedgeParametersChips(this.setup.calls, this.setup.puts);
    }

    get biasCrumbs(): MavenChip[] {
        return biasChips(this.setup.dates);
    }

    // ---- Override Market Indicators (CR-20260915-001) ------------------------------------------
    //
    // THE SETUP OWNS THE MAP AND THE RESET; the block is a view over it. `setup.marketOverrides`
    // holds edited dates only, keyed by 'yyyy-MM-dd'; the child emits a fresh map on every edit
    // and clear, and this component writes it back. The child is created and destroyed with the
    // section body, so the map has to live here or a collapse would lose it.
    //
    // THE DATES THE BLOCK STEPS THROUGH are the dates the run TARGETS: the questionnaire's
    // surviving dates (single date, or start..end minus the weekday and specific exclusions, with
    // lifted Mondays back in) INTERSECTED with the chain's listed expirations — the same
    // intersection the ORION (OPG) normalizer performs, so the block never offers a date that
    // cannot be run. The backend's own candidate-day resolution (landmines, calendar) may drop
    // more; an override on a dropped date is simply never consumed. Memoized BY CONTENT, so the
    // OnPush child sees a new reference only when a date actually joined or left.
    //
    // Date-selector changes temporarily replace overrides with market readings. Remember the
    // working values against the selector state that produced them, so reversing the cause
    // reverses that automatic effect. Manual edits/clears become that state's working values;
    // they must never be replaced with a fresh copy of the selected template.
    // Calls/Puts visibility and unrelated Setup parameters do not enter this relationship.

    private _overrideDateKeys: string[] = [];

    get overrideDateKeys(): string[] {
        return this._overrideDateKeys;
    }

    // The portfolio's underlying, e.g. "SPX": the symbol the block's IV query and vertical-skew
    // subscription resolve against. Set beside the strike-picker source, cleared with it.
    underlying: string | null = null;

    // The chain's own descriptors, for the block's DTE badge — the backend's daysToExpiration,
    // the same number every other surface renders, never computed locally.
    expirationDescriptors: OptionExpirationDescriptor[] = [];

    get callsEnabled(): boolean {
        return !!(this.setup.calls && this.setup.calls.enabled);
    }

    get putsEnabled(): boolean {
        return !!(this.setup.puts && this.setup.puts.enabled);
    }

    get overridesCrumbs(): MavenChip[] {
        return overridesChips(this._overrideDateKeys, this.setup.marketOverrides,
            this.callsEnabled, this.putsEnabled);
    }

    @DetectMethodChanges()
    onOverridesChanged(next: MarketConditionOverrides): void {
        const previous = this.setup.marketOverrides || {};
        const incoming = next || {};
        // A manual edit/clear is authoritative for that date wherever it was remembered.
        // Copy only changed records; untouched dates in the child's full map are not edits.
        const dates = Object.keys({...previous, ...incoming});
        dates.forEach(date => {
            const before = previous[date];
            const after = incoming[date];
            const changed = !!before !== !!after || (before && after
                && OVERRIDE_VALUE_KEYS.some(field => before[field] !== after[field]));
            if (!changed) { return; }
            Object.keys(this._overrideStates).forEach(key => {
                const state = this._overrideStates[key];
                if (state.dateKeys.indexOf(date) < 0) { return; }
                if (after) { state.overrides[date] = {...after}; }
                else { delete state.overrides[date]; }
            });
        });
        this.setup.marketOverrides = next || {};
        this.backendProblem = null;
        this.refreshChangedKeys();
    }

    private refreshOverrideDateKeys(): void {
        const next = this.computeOverrideDateKeys();
        const selectorKey = this.overrideSelectorKey(next);
        if (selectorKey === this._overrideSelectorKey) {
            return;
        }
        if (this._overrideSelectorKey !== null) {
            this._overrideStates[this._overrideSelectorKey] = {
                dateKeys: this._overrideDateKeys.slice(),
                overrides: this.copyOverrides(this.setup.marketOverrides),
                allDates: captureMavenIndicators(this._allDatesOverride)
            };
        }
        const remembered = this._overrideStates[selectorKey];
        // Keep the child input stable when only boundaries/mode changed, not its visible dates.
        if (next.join('|') !== this._overrideDateKeys.join('|')) { this._overrideDateKeys = next; }
        this._overrideSelectorKey = selectorKey;
        this._allDatesOverride = remembered ? captureMavenIndicators(remembered.allDates) : null;
        this.setup.marketOverrides = remembered ? this.copyOverrides(remembered.overrides) : {};
        this.refreshChangedKeys();
    }

    private overrideSelectorKey(dateKeys: string[]): string {
        const selection = this.setup.dates && this.setup.dates.date;
        const key = (date: Date | null) => date instanceof Date && !isNaN(date.getTime()) ? dayKey(date) : '';
        const selector = !selection ? [] : selection.kind === 'single'
            ? ['single', key(selection.single)] : ['range', key(selection.start), key(selection.end)];
        // Exclusion edits matter only if they change the override dates. Bias and side settings
        // cannot cause automatic override changes and are deliberately absent from this key.
        return JSON.stringify([selector, dateKeys]);
    }

    private copyOverrides(source: MarketConditionOverrides): MarketConditionOverrides {
        const copy: MarketConditionOverrides = {};
        Object.keys(source || {}).forEach(key => { copy[key] = {...source[key]}; });
        return copy;
    }

    private clearOverrideHistory(): void {
        this._overrideStates = {};
        this._overrideSelectorKey = null;
    }

    private computeOverrideDateKeys(): string[] {
        const dates = this.setup.dates;
        if (!dates || !dates.date || this.availableExpirations.length === 0) {
            return [];
        }
        const listed = this.availableExpirations;
        const surviving = survivingDates(dates);
        const out: string[] = [];
        for (let i = 0; i < surviving.length; i++) {
            const key = dayKey(surviving[i]);
            if (listed.indexOf(key) >= 0 && out.indexOf(key) < 0) {
                out.push(key);
            }
        }
        return out;
    }

    // ---- Expand all / Collapse all (owner vrequest 2026-09-15) ----------------------------------
    //
    // ONE clickable text on a pinned stripe above the sections, the Results "Sort by" stripe's
    // twin. It moves every section AND both Calls / Puts subsections at once.
    //
    // THE LABEL RULE (owner, 2026-09-15): the label is the OPPOSITE of the last click and stays
    // there until the next click — a chevron click in between does not move it. Two exceptions
    // override that memory: when EVERYTHING is open the label reads "Collapse all", when
    // EVERYTHING is closed it reads "Expand all", however the trader got there.
    //
    // The subsections are the child's `expanded` flag — presentation only, never sent. Expand all
    // opens an UNCHECKED side's body too; it never touches the checkbox (`enabled`).
    //
    // ⚠ THE SUBSECTIONS ARE "WEAK" (owner, 2026-09-15, second pass): the CLICK moves them, but the
    // two automatic overrides read the FIVE SECTIONS ONLY. Five open sections with a folded side
    // still read "Collapse all"; a side's chevron never flips the label.

    private static readonly SECTION_KEYS: ReadonlyArray<MavenSetupPanelSection> =
        ['portfolio', 'settingsTemplate', 'dates', 'existingHedges', 'hedgeParameters', 'overrides', 'bias'];

    // Only rendered while the Hedge Parameters section is open (its body is an *ngIf).
    @ViewChildren(MavenSideParametersComponent) private _sides: QueryList<MavenSideParametersComponent> | undefined;

    private _lastToggleAll: 'expand' | 'collapse' | null = null;

    private get allSectionsOpen(): boolean {
        return MavenSetupComponent.SECTION_KEYS.every(k => this.isOpen(k));
    }

    private get allSectionsClosed(): boolean {
        return MavenSetupComponent.SECTION_KEYS.every(k => !this.isOpen(k));
    }

    // True = the next click collapses (label "Collapse all"); false = it expands.
    get toggleAllCollapses(): boolean {
        if (this.allSectionsOpen) {
            return true;
        }
        if (this.allSectionsClosed) {
            return false;
        }
        // Mixed: the opposite of the last click. Before any click the column starts fully open,
        // so a mixed state can only come from chevrons — offer to collapse the rest.
        return this._lastToggleAll !== 'collapse';
    }

    get toggleAllLabel(): string {
        return this.toggleAllCollapses ? 'Collapse all' : 'Expand all';
    }

    @DetectMethodChanges()
    toggleAll(): void {
        const open = !this.toggleAllCollapses;
        this._lastToggleAll = open ? 'expand' : 'collapse';
        MavenSetupComponent.SECTION_KEYS.forEach(k => this._open[k] = open);
        // The side blocks are created by their section body's *ngIf (the Date Range body is the
        // one exception, kept alive), so on an expand they may not exist yet: render the sections
        // first, then reach the children.
        this._changeDetector.detectChanges();
        if (this._sides) {
            this._sides.forEach(s => s.setExpanded(open));
        }
    }

    // ---- Required markers (vrequest 2026-09-13, item 3) -----------------------------------------
    //
    // A single-field requirement is shown as a RED ASTERISK beside the element's label, not as a
    // red sentence. The sentence path (`sentencesAt`) keeps only what an asterisk cannot say: the
    // range cap and the backend's refusals.

    private static readonly STARRED_CODES: { [placement: string]: string } = {
        portfolio: 'noPortfolio',
        template: 'noTemplate',
        dates: 'noDate'
    };

    isRequired(placement: 'portfolio' | 'template' | 'dates'): boolean {
        const code = MavenSetupComponent.STARRED_CODES[placement];
        return this.problems.some(p => p.placement === placement && p.code === code);
    }

    // The section title's star. Dates is the one section whose element belongs to another
    // component, so its star lives on the title.
    sectionRequired(section: string): boolean {
        return section === 'dates' && this.isRequired('dates');
    }

    // The sentences still worth a line at this placement: everything except the one the asterisk
    // already says.
    sentencesAt(placement: 'portfolio' | 'template' | 'dates'): string[] {
        const starred = MavenSetupComponent.STARRED_CODES[placement];
        const local = problemsAt(this.problems, placement)
            .filter(p => p.code !== starred)
            .map(p => p.message);
        if (this.backendProblem && this.backendProblem.placement === placement) {
            local.push(this.backendProblem.message);
        }
        return local;
    }

    async ngOnInit(): Promise<void> {
        // Portfolio loading is started by the parent after the complete Maven panel paints.
        this.refreshExpectedRangeAvailability();

        // App Settings saved (Expected Move toggled, Custom Percent fixed, ...): the Expected
        // Range coverage pill follows the new settings at once, without reopening the panel.
        // Same source the OPG copilot listens to.
        this._messageBus.of(AppSettingsUpdatedMessageTopic)
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(() => this.refreshExpectedRangeAvailability());

        // An explicit "set as current zone" from the footer indicator's pop-up (the same message
        // the APG listens to). The message carries the value, so no re-query. Filtered to the
        // SELECTED portfolio by the composite key — another portfolio's zone is not this line's.
        this._messageBus.of<CurrentZoneChangedMessage>('CurrentZoneChanged')
            .pipe(
                takeUntil(this._unsubscriber),
                filter(x => !isVoid(this.selectedPortfolio)
                    && x.payload.portfolioKey === makePortfolioKey(this.selectedPortfolio))
            )
            .subscribe(x => this.setCurrentZone(x.payload.currentZone));

        // CR-20260916-004: every stored user setting echoes back from the shell (own saves
        // included); the watch picks out the run's positions / hedge-book keys. Subscribed once,
        // for the panel's life — the baseline decides whether an echo means anything.
        this._userSettings.userSettingChanged$
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(x => this.onUserSettingEcho(x));

        // CR-20260916-005: the day flipped (replay cursor stepped over midnight, a jump, or the
        // server time) — the anchor's clamp to today may have moved.
        this._shellToday.changed$
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(() => this.onTodayChanged());
    }

    ngOnDestroy(): void {
        this._selectionGeneration++;
        // Release the pre-warm lease as the panel closes. The backend also releases it when the
        // session ends, so a panel that dies without this (crash, killed tab) still leaks nothing.
        this.releaseWarm();
        this._unsubscriber.next();
        this._unsubscriber.complete();
    }

    // ---- The Current Zone line --------------------------------------------------------------

    get hasZone(): boolean {
        return this.currentZone !== null;
    }

    // The number, or N/A. The words "Current" and "Zone" are the template's.
    get displayZone(): string {
        return this.hasZone ? '' + this.currentZone : 'N/A';
    }

    // Stale = a run was sent for THIS portfolio, and the zone has moved since. Nothing before the
    // first run, and never for an N/A: an N/A already blocks Apply and says so in red.
    get zoneStale(): boolean {
        return this.lastRunZone !== null && this.hasZone && this.currentZone !== this.lastRunZone;
    }

    @DetectMethodChanges()
    private setCurrentZone(zone: number | null | undefined): void {
        this.currentZone = isValidNumber(zone as number, true) ? (zone as number) : null;
    }

    // The read the APG does on its own portfolio selection, done the same way: the COMPOSITE key
    // (a bare id matches no assignment and comes back "no zone" for a portfolio that has one —
    // the G1 defect), and N/A the moment the portfolio changes, so the old number never shows
    // against the new portfolio while the read is in flight. A failed read is N/A, not a throw:
    // the line then blocks Apply, and the backend's own NoZone refusal stays the second gate.
    private async refreshCurrentZone(portfolio: ApgPortfolio | null): Promise<void> {
        this.setCurrentZone(null);
        // The LATEST portfolio asked for. ⚠ NOT `selectedPortfolio`: that is written by
        // applyPortfolioSelection only after the template reads finish, and this read comes back
        // FIRST — compared against it, every reply looked late and was dropped (owner smoke,
        // 2026-09-14: N/A on a portfolio whose footer showed 7680).
        this._zoneReadFor = portfolio;

        if (!portfolio) {
            return;
        }

        const key = makePortfolioKey(portfolio);
        if (isVoid(key)) {
            return;
        }

        let zone: number | null = null;
        try {
            const reply = await this._shellClient
                .processQuery<GetPortfolioCurrentZoneDataReply>(new GetPortfolioCurrentZoneData(key));
            zone = isValidNumber(reply?.currentZone, true) ? reply.currentZone : null;
        } catch {
            zone = null;
        }

        // The trader may have moved on to another portfolio while the read was out: a late reply
        // for the old one must not land on the new one's line.
        if (this._zoneReadFor === portfolio) {
            this.setCurrentZone(zone);
        }
    }

    private _zoneReadFor: ApgPortfolio | null = null;

    // ---- The Portfolio Zone --------------------------------------------------------------------

    get hasPortfolioZone(): boolean {
        return hasPortfolioZone(this.portfolioZone);
    }

    // One strike, "call / put" when they differ, or N/A.
    get displayPortfolioZone(): string {
        return formatPortfolioZone(this.portfolioZone);
    }

    // The saved positions of the portfolio, under the owner's key, the way the run-data watch
    // finds them. On a portfolio change: N/A first, then the read. On a settings echo: the
    // number stays while the re-read is out (nothing is stale about it yet), and the echoed value
    // is preferred when it is the resolved key's — for another trader's portfolio the service's
    // own index holds the login-time copy, and the echo is the only live one. The same
    // latest-portfolio guard as the zone read, since the key resolution is async (it reads the
    // owner's last-used template).
    private async refreshPortfolioZone(portfolio: ApgPortfolio | null, echo?: UserSettingObject): Promise<void> {
        if (!echo) {
            this.setPortfolioZone(NO_PORTFOLIO_ZONE);
        }
        this._portfolioZoneReadFor = portfolio;

        if (!portfolio) {
            return;
        }

        let saved: BeforePositionDto[][] | null = null;
        try {
            const key = await this._apgDataService.getPortfolioPositionsKey(portfolio);
            if (key) {
                saved = (echo && echo.key === key)
                    ? echo.value
                    : this.readSavedPositions(key, portfolio.userId);
            }
        } catch (e) {
            console.error('Maven: portfolio zone not read; the line shows N/A', e);
            saved = null;
        }

        if (this._portfolioZoneReadFor === portfolio) {
            this.setPortfolioZone(readPortfolioZone(saved, t => this._optionChains.isOptionExpired(t)));
        }
    }

    // The live copy first (every echo lands there, own saves included), the login-time copy
    // when nothing has echoed for the key since login.
    private readSavedPositions(key: string, userId: string): BeforePositionDto[][] | null {
        const live = this._userSettings.getValue<BeforePositionDto[][]>(key, userId, 'their-current');
        if (!isVoid(live)) {
            return live;
        }
        return this._userSettings.getValue<BeforePositionDto[][]>(key, userId);
    }

    private _portfolioZoneReadFor: ApgPortfolio | null = null;

    @DetectMethodChanges()
    private setPortfolioZone(zone: MavenPortfolioZone): void {
        this.portfolioZone = zone;
    }

    @DetectMethodChanges()
    private refreshExpectedRangeAvailability(): void {
        this.expectedRangeAvailable = this._expectedMove.isExpectedRangeUsableForCoPilot();
    }

    // ---- Validation and placement -----------------------------------------------------------

    get limits(): MavenSetupLimits {
        const lim = noLimits();
        // Maven's OWN shell-level "Max Query Range (days)" (MAVEN Settings dialog, owner
        // 2026-09-14 - it used to borrow the APG ORION's). Root-provided, so no module change
        // and no second copy of the number.
        lim.maxRangeDays = this._orionSettings.mavenMaxQueryRangeDays;
        // The selected portfolio's current zone, as the Current Zone line shows it. N/A refuses
        // Apply here, before the wire, in the backend's own words (vrequest 2026-09-14).
        lim.currentZone = this.currentZone;
        // CR-20260916-003: what the Close list holds per expiration, so the validator can say
        // whether a toggled expiration has a selected hedge in it. Null until the list reports.
        lim.closeHedgesByExpiration = this.closeBook ? this.closeBook.hedgesByExpiration : null;
        lim.closeExpirationLabels = this.closeBook ? this.closeBook.labels : null;
        // The end-strike rule (R-H) is NOT here: leader ruling L11 puts it in the backend
        // validator, which holds the saved positions and the zone that the rule needs. This panel
        // sends ids only and surfaces the refusal in place, as it surfaces NoZone.
        return lim;
    }

    get problems(): MavenSetupProblem[] {
        return validateMavenSetup(this.setup, this.limits);
    }

    get canRun(): boolean {
        return !this.isRunning && !this.templateBusy && !this.expirationsLoading && canApply(this.setup, this.limits);
    }

    // ⚠ NO "active section" any more (owner, vrequest 2026-09-13 round 3). The validator's
    // activeSection() still exists for anyone who needs the first unfinished section, but the
    // Setup no longer paints it: the blue border and title read as an alarm, not a hint.

    // Messages for one control: the local ones, plus the backend's if it landed there.
    messagesAt(placement: MavenRefusalPlacement): string[] {
        const local = problemsAt(this.problems, placement).map(p => p.message);
        if (this.backendProblem && this.backendProblem.placement === placement) {
            local.push(this.backendProblem.message);
        }
        return local;
    }

    // ---- S1, portfolio ----------------------------------------------------------------------

    @DetectMethodChanges({isAsync: true})
    async loadInitialPortfolio(): Promise<void> {
        this.loadPortfolios();
        const userId = (this._sessionService.sessionData || {}).userId;
        const ownGroup = this.portfolioGroups.filter(group => group.userId === userId)[0];
        if (ownGroup && ownGroup.items.length === 1) {
            // Use the full selection path; the dropdown ignores programmatic change events.
            await this.selectPortfolio(ownGroup.items[0]);
        }
    }

    @DetectMethodChanges()
    private loadPortfolios(): void {
        // ONE implementation of the grouped list, shared (buildPortfolioGroups), rather than a
        // fifth copy of a pattern already duplicated across four panels. Alan's ruling at G3.
        //
        // ⚠ WHAT THIS REPLACES, and why the old code failed his smoke: it looped
        // sessionData.users and stopped there. That list DELIBERATELY EXCLUDES THE LOGGED-IN USER,
        // so a super user saw every colleague's portfolios and none of his own — and it passed
        // every gate, because nothing automated knows what the list is supposed to contain. It
        // also passed `undefined` as the own-user name, so self's group would have been headerless
        // even once self was added.
        //
        // The helper owns the add-self case, self's locally-synthesized display name, the ordering
        // (own first, then the others by name) and the dedupe. This method now does one thing:
        // hand it a reader that configures the per-module portfolios service for the identity it
        // is asked about.
        const session = this._sessionService;
        const data: any = session.sessionData || {};

        this.portfolioGroups = buildPortfolioGroups(
            {
                userId: data.userId,
                firstName: data.firstName,
                lastName: data.lastName,
                users: data.users || []
            },
            session.isSuperUser,
            cfg => {
                // configure() immediately before the read, in the same synchronous block — the
                // service is shared at root with other panels, so nothing may interleave between
                // the two.
                this._portfoliosService.configure(cfg);
                return this._portfoliosService.getPortfolios() || [];
            });
    }

    async onPortfolioChanged(e: any): Promise<void> {
        if (!e || !e.event) {
            return;   // programmatic echo
        }
        await this.selectPortfolio(e.value as ApgPortfolio);
    }

    @DetectMethodChanges()
    private applyPortfolioSelection(
        portfolio: ApgPortfolio | null,
        templates: ICashFlowAdjustmentSettingsTemplate[],
        templateId: string | null,
        defaultQty: number | null
    ): void {
        this.selectedPortfolio = portfolio;
        // The Details column cannot open either order pop-up without it.
        this.portfolioChange.emit(portfolio);
        this.setup.portfolioId = portfolio ? portfolio.id : null;
        // THE OWNER, not the login. Every saved-state read on the backend follows this.
        this.setup.portfolioOwnerId = portfolio ? (portfolio.userId || null) : null;
        this.templates = templates;
        this.setup.templateId = templateId;
        this.defaultQty = defaultQty;
        // A new portfolio invalidates whatever the last run said about the old one — and, since
        // the owner's 2026-09-15 ruling, everything else too: selectPortfolio runs the full Reset
        // before this is reached, so the baseline, the zone, the cards and every input are
        // already gone. This only re-arms the refusal slot.
        this.backendProblem = null;
    }

    private async selectPortfolio(portfolio: ApgPortfolio | null): Promise<void> {
        if (!portfolio) {
            // Clearing the box is the Reset button by another gesture.
            this.onReset();
            return;
        }

        // THE RESET FIRST (owner ruling 2026-09-15): a portfolio change clears every field and
        // every card exactly as the Reset button does, then loads the new portfolio onto the
        // blank Setup. Its date questionnaire reset emits, its columns blank, its baseline goes.
        this.resetForPortfolioChange(portfolio);
        const generation = this._selectionGeneration;

        // The zone read goes out FIRST and in parallel with the template read below: it sets N/A
        // synchronously (never the old portfolio's number) and lands when it lands. Not awaited —
        // the templates are what the trader is waiting on, and the zone line repaints on its own.
        this.refreshCurrentZone(portfolio);
        // The Portfolio Zone the same way: N/A now, its own read in flight, repaints on its own.
        this.refreshPortfolioZone(portfolio);

        // Read for the PORTFOLIO'S OWNER. ApgDataService already configures its own dependencies
        // with portfolio.userId on every call, so this follows the portfolio rather than the login
        // without Maven having to arrange it.
        await this._templatesService.configure({
            orientation: undefined,
            userId: portfolio.userId,
            userName: portfolio.userName
        });

        // The SAVED index, plus this portfolio's DEFAULT template.
        //
        // ⚠ Nobody stores default templates: the id IS the template (`----^{underlying}^{strategy}`)
        // and the service synthesises one on demand from the pair — the same call Sweet Price
        // Tracking makes. Maven builds no parallel list; it asks for the one default belonging to
        // this portfolio's pair. Without this, a portfolio with no saved templates offered NOTHING
        // and could not be run at all, which is what Alan hit.
        //
        // THE PAIR, MEASURED (the model declares both optional): a portfolio created through the
        // APG's own dialog ALWAYS carries `asset` and `strategy` — the dialog refuses to create one
        // without either ("Provide portfolio asset" / "Provide portfolio strategy").
        //
        // History: CR-20260916-005 read the synthetic `<No Portfolio>` default's pair from its
        // last-used template because its stored `asset` was stale (owner smokes 2026-09-16).
        // CR-20260928-001: the scratchpad is gone; EVERY portfolio's own pair is authoritative. The
        // last-used template still seeds the S2 pre-fill below.
        if (generation !== this._selectionGeneration) { return; }
        const lut = await this.readLastUsedTemplate(portfolio);
        if (generation !== this._selectionGeneration) { return; }
        const pairUnderlying = portfolio.asset;
        const pairStrategy = portfolio.strategy;
        const saved = this._templatesService.getTemplates() || [];
        const templates = withDefaultTemplate(
            saved,
            this._templatesService.getOrCreateDefaultTemplate(pairUnderlying, pairStrategy));

        // S2: PRE-FILLED from the APG's per-portfolio last-used template, and ALWAYS VISIBLE. The
        // pre-fill is a starting point the trader can see and change — never a silent default,
        // which is the thing S2 forbids by name.
        //
        // ⚠ THE DEFAULT TEMPLATE IS NOW IN THE LIST, AND THAT MUST NOT MAKE IT THE SELECTION.
        // The match below is driven entirely by the LAST-USED template: if the trader last used
        // the default, it pre-fills, which is correct — he chose it. If nothing matches, the
        // selection stays NULL and the Setup refuses Apply with "Choose an adjustment template."
        // There is deliberately no `templateId || defaultTemplate.templateId` anywhere: offering
        // is not choosing.
        const match = templates.filter(t => !!lut && t.templateId === lut.templateId);
        const templateId: string | null = match.length > 0 ? match[0].templateId : null;

        let defaultQty: number | null = null;
        try {
            defaultQty = await this._apgDataService.getDefaultQtyForPortfolio(portfolio);
        } catch (e) {
            // No usable quantity is a REAL state, not an error to paper over: a normalized money
            // entry then cannot be converted, and the order carries the refusal sentinel the
            // backend rejects by name. Guessing 1 here would run the engine on a made-up base.
            defaultQty = null;
        }

        if (generation !== this._selectionGeneration) { return; }
        this.applyPortfolioSelection(portfolio, templates, templateId, defaultQty);

        await this.resolveStrikePickerSource(portfolio, generation);
        if (generation === this._selectionGeneration) { this.finishTemplateFamilyLoad(); }
    }

    // The end-strike picker's two inputs AND the date questionnaire's expiration list, both
    // resolved from the portfolio's own underlying — because the strikes and the roll dates a
    // trader may pick are the ones that underlying actually lists.
    private async resolveStrikePickerSource(portfolio: ApgPortfolio, generation: number): Promise<void> {
        this.setExpirations([], true);
        try {
            const underlying = await this._apgDataService.getUnderlyingOfPortfolio(portfolio);
            if (generation !== this._selectionGeneration) { return; }
            if (!underlying) {
                this.setStrikePickerSource(null, null);
                this.setExpirations([], false);
                return;
            }

            const instrument = this._tradingInstruments.getInstrumentByTicker(underlying);
            const chain = await this._optionChains.getChain(underlying);
            if (generation !== this._selectionGeneration) { return; }
            const expirations = (chain && chain.expirations) ? chain.expirations : [];

            // CR-20260915-001: what the override block resolves its readings against, and the
            // descriptors its date label reads the DTE from.
            this.underlying = underlying;
            this.expirationDescriptors = expirations;

            // The NEAREST expiration that has not passed. `daysToExpiration` is the chain's own
            // figure, so this follows the Quote Generator cursor without Maven knowing about it.
            let nearest: OptionExpirationDescriptor | null = null;
            for (let i = 0; i < expirations.length; i++) {
                const e = expirations[i];
                if (e.daysToExpiration < 0) {
                    continue;
                }
                if (!nearest || e.daysToExpiration < nearest.daysToExpiration) {
                    nearest = e;
                }
            }

            this.setStrikePickerSource(instrument || null, nearest);

            // The candidate roll dates the questionnaire offers: every listed expiration, as
            // 'yyyy-MM-dd' keys. It restricts the date boxes to tradeable days and drives the
            // exclusion calendar.
            const keys: string[] = [];
            for (let i = 0; i < expirations.length; i++) {
                if (expirations[i].optionExpirationDate) {
                    keys.push(expirations[i].optionExpirationDate);
                }
            }
            this.setExpirations(keys, false);

            // The Start anchor: the SHORT-OPTION expiration of the portfolio's saved position.
            await this.resolveAnchor(portfolio, generation);
        } catch (e) {
            if (generation !== this._selectionGeneration) { return; }
            // No chain means no strike list and no date list. Both controls disable themselves,
            // which is the honest state — better than offering a ladder or a calendar that is not
            // this portfolio's.
            this.setStrikePickerSource(null, null);
            this.setExpirations([], false);
            this._shortExpirationKey = null;
            this.setAnchorKey(null);
        }
    }

    // The APG's per-portfolio last-used template, or null. A failed read is "none", never a throw:
    // the caller then seeds from the application defaults and pre-fills nothing.
    private async readLastUsedTemplate(portfolio: ApgPortfolio): Promise<LastUsedTemplate | null> {
        try {
            const lut = await this._apgDataService.getLastUsedTemplateOfPortfolio(portfolio);
            return (lut && !isVoid(lut.templateId)) ? lut : null;
        } catch (e) {
            return null;
        }
    }

    private async resolveAnchor(portfolio: ApgPortfolio, generation: number): Promise<void> {
        try {
            const shortOption = await this._apgDataService.getShortOptionTicker(portfolio);
            if (generation !== this._selectionGeneration) { return; }
            // The ticker's own expiration, already a calendar key. Read from the SAVED position,
            // which is the same state the backend reads for the run — so the Start the trader is
            // anchored to and the Start the run resolves cannot disagree.
            this._shortExpirationKey = (shortOption && shortOption.expiration) ? shortOption.expiration : null;
        } catch (e) {
            if (generation !== this._selectionGeneration) { return; }
            this._shortExpirationKey = null;
        }
        // CR-20260916-005: "today" is resolved for THIS portfolio's underlying — the QG replay
        // cursor in a QG shell (asked of the shell itself when the QG panel is closed), else the
        // shell's server time. Awaited so the first seed already clamps.
        await this._shellToday.refresh(this.underlying || undefined);
        if (generation === this._selectionGeneration) { this.setAnchorKey(this.clampedAnchorKey()); }
    }

    // CR-20260916-005 (owner ruling 1, 2026-09-16: clamp to today, as the APG does). The anchor is
    // the short-option expiration, or TODAY when that expiration is already behind the
    // authoritative clock — the same rule as the APG's readRollAnchorKey, so a Maven Start is never
    // seeded on a day the replay has already passed. Keys are 'yyyy-MM-dd', so a string compare
    // is a day compare. No short option = no anchor (the trader picks), unchanged.
    private clampedAnchorKey(): string | null {
        const short = this._shortExpirationKey;
        if (!short) {
            return null;
        }
        const today = this._shellToday.todayKey(this.underlying || undefined);
        return today && today > short ? today : short;
    }

    // The replay cursor moved a day (or the server time did): the anchor may now clamp elsewhere.
    // Tell the questionnaire the way the APG does on a positions edit — it re-reads the anchor and
    // rolls a Start that fell behind it.
    private onTodayChanged(): void {
        if (!this.selectedPortfolio) {
            return;
        }
        // setAnchorKey tells the questionnaire when the key actually moved.
        this.setAnchorKey(this.clampedAnchorKey());
        // CR-20260918-005: the shell re-evaluates the market zone for the cursor's day; re-read
        // it so the line (and the stale border against the last run) shows that day's zone.
        this.refreshCurrentZone(this.selectedPortfolio);
    }

    private _shortExpirationKey: string | null = null;

    @DetectMethodChanges()
    private setExpirations(keys: string[], loading: boolean): void {
        this.availableExpirations = keys;
        this.expirationsLoading = loading;
        // CR-20260915-001: the listed expirations are half of the override block's date list.
        this.refreshOverrideDateKeys();
        this.refreshChangedKeys();
    }

    @DetectMethodChanges()
    private setAnchorKey(key: string | null): void {
        const changed = key !== this._anchorKey;
        this._anchorKey = key;
        // CR-20260916-005 (owner smoke 2026-09-16: the seed appeared only after clicking "Single
        // Date"): the anchor is resolved AFTER the expiration list is handed over, and the
        // questionnaire seeds on the list's arrival — with no anchor yet, no seed. The anchor is a
        // reader, so a stored value alone tells the questionnaire nothing; it must be told, the
        // way the APG tells it on a positions edit. It re-reads and seeds / rolls what needs it.
        if (changed && this._datesQuestionnaire) {
            this._datesQuestionnaire.onAnchorChanged();
        }
        this.refreshChangedKeys();
    }

    @DetectMethodChanges()
    private setStrikePickerSource(
        instrument: TradingInstrument | null,
        expiration: OptionExpirationDescriptor | null
    ): void {
        this.targetInstrument = instrument;
        this.strikeExpiration = expiration;
    }

    // ---- S2, template -----------------------------------------------------------------------

    @DetectMethodChanges()
    onTemplateChanged(e: any): void {
        if (!e || !e.event) {
            return;   // programmatic echo — the pre-fill writing itself back
        }
        // The picker carries valueExpr="templateId", so the value IS the id. The template BODY
        // never leaves this panel: the order sends the id and the backend resolves it for the
        // portfolio's owner (contract M1 section 3, A5).
        const id = e.value;
        this.setup.templateId = (typeof id === 'string' && id.length > 0) ? id : null;
        this.backendProblem = null;
        this.refreshChangedKeys();
    }

    // ---- S3, dates --------------------------------------------------------------------------

    // The questionnaire component owns the date rules and emits {inputs, valid}; this holds the
    // inputs and lets the shared validation judge them. `valid` is deliberately not stored: the
    // APG's own completeness helper also gates on the directional bias, which is inert here.
    @DetectMethodChanges()
    onDatesChanged(state: { inputs: RollPositionsInputs; valid: boolean }): void {
        if (!state || !state.inputs) {
            return;
        }
        this.setup.dates = state.inputs;
        this.backendProblem = null;

        // CR-20260915-001: the other half of the override block's date list — and the reset
        // trigger (ruling #4) when the targeted dates moved.
        this.refreshOverrideDateKeys();
        this.refreshChangedKeys();

        // Tell the backend the range so it warms the run's skew slots ahead of Apply (option 2).
        this.sendWarm();
    }

    // THE PANEL LEASE (owner ruling 2026-09-14, option 2). Sends the current date range so the
    // backend holds the run's skew slots warm. Best-effort: a failed warm is a lost optimisation,
    // never a user-facing error, so the send is fire-and-forget with a swallowed rejection. The
    // lease is keyed by (session, this panel) on the backend; sending again just recomputes it.
    private sendWarm(): void {
        const sel = this.setup.dates ? this.setup.dates.date : null;
        let dateKind = '';
        let date: string | null = null;
        let rangeStart: string | null = null;
        let rangeEnd: string | null = null;

        if (sel && sel.kind === 'single') {
            dateKind = 'single';
            date = sel.single instanceof Date ? toCalendarDateString(sel.single) : null;
        } else if (sel && sel.kind === 'range') {
            dateKind = 'range';
            rangeStart = sel.start instanceof Date ? toCalendarDateString(sel.start) : null;
            rangeEnd = sel.end instanceof Date ? toCalendarDateString(sel.end) : null;
        }

        this.warm(dateKind, date, rangeStart, rangeEnd);
    }

    // Release the lease: an empty declaration. Sent on destroy so a closed panel warms nothing.
    private releaseWarm(): void {
        this.warm('', null, null, null);
    }

    // KILL SWITCH (owner ruling 2026-09-14): the panel pre-warm is DISABLED, not removed. It caused
    // problems in real-time (live feed) sessions. While false, no warm command leaves the panel;
    // the backend handler carries its own switch too. Kept intact for re-enabling.
    private static readonly PANEL_WARM_ENABLED = false;

    private warm(dateKind: string, date: string | null, rangeStart: string | null,
                 rangeEnd: string | null): void {
        if (!MavenSetupComponent.PANEL_WARM_ENABLED || !this.panelInstanceId) {
            return;
        }

        const command = new WarmMavenSlots(
            MAVEN_CONTRACT_VERSION, this.panelInstanceId, dateKind, date, rangeStart, rangeEnd);

        try {
            this._shellClient.processCommand(command).catch(() => void 0);
        } catch {
            // Best-effort only.
        }
    }

    // The chain-listed expirations the questionnaire offers as candidate roll dates, and gates its
    // pickers on. Empty until a portfolio is chosen — the component falls back to disabling
    // weekends only, which is its own documented behaviour, not something Maven arranges.
    availableExpirations: string[] = [];

    expirationsLoading = false;

    // The cap, Maven's OWN shell-level setting (2026-09-14). One source; Maven keeps no copy of
    // the number.
    get maxRangeDays(): number | null {
        return this._orionSettings.mavenMaxQueryRangeDays;
    }

    // CR-20260915-002: Maven's OWN "Weekdays to exclude" - the STARTING weekday-exclusion set of
    // the Date Range (default NONE: the stakeholders ruled Maven must not exclude Monday). One
    // source, the same array instance until the next save (a stable [input] binding); the date
    // block reads it at its seeding moments - open, Reset, Single -> Range.
    get defaultWeekdayExclusions(): Weekday[] {
        return this._orionSettings.mavenWeekdayExclusions;
    }

    // THE START ANCHOR — the portfolio's SHORT-OPTION expiration, as a 'yyyy-MM-dd' key.
    //
    // A READER, not a value, and the questionnaire asks for it as one on purpose: the shorts are
    // edited while the panel is open and the Quote Generator cursor moves, so a value captured
    // once would anchor the Start to a day that has since stopped being the right one. Null = no
    // short option on this portfolio, which means no seed and no lower bound — the trader picks.
    readonly anchorKeyReader = (): string | null => {
        return this._anchorKey;
    }

    private _anchorKey: string | null = null;

    // ---- S4, existing hedges ----------------------------------------------------------------

    readonly existingHedgesButtons = [
        {id: 'ignore', text: 'Ignore'},
        {id: 'close', text: 'Close'}
    ];

    get existingHedgesKeys(): string[] {
        return [this.setup.existingHedges];
    }

    @DetectMethodChanges()
    onExistingHedgesClick(e: any): void {
        if (!e || !e.itemData) {
            return;
        }
        this.setup.existingHedges = e.itemData.id;
        // CR-20260916-003 (owner, 2026-09-16, on the first build): THE SELECTION SURVIVES THE
        // SWITCH. Flipping Close -> Ignore -> Close must bring the same expirations and hedges
        // back; only Reset and a portfolio change clear it (clearEverything replaces the Setup).
        // Under Ignore the selection is inert: the order sends two empty arrays, the validator
        // and the diff skip it, the cradle shows the plain chip. The child list is HIDDEN, not
        // destroyed (second build, same day): its model, pricer and subscriptions stay, so the
        // switch back is instant and shows the same figures.
        this.backendProblem = null;
        this.refreshChangedKeys();
    }

    // ---- S10, directional bias — VISIBLE AND INERT ------------------------------------------
    //
    // ITS OWN SECTION since 2026-09-13 (owner ruling, vrequest round 3: "it does not belong to
    // the hedge parameters") — this REPLACES leader ruling L42's placement inside Hedge
    // Parameters; L42's other half stands: it is S6's, a Setup parameter, not a Details
    // placeholder. Ruling L47 says an inert placeholder is never a gate,
    // so nothing here reaches the Apply gate, and contract M1 section 8 gives it no wire field —
    // the order builder passes it nowhere, deliberately and with a comment saying so.
    //
    // The value is held on the SAME RollPositionsInputs the date questionnaire owns, because that
    // type already carries a bias and minting a second one would create two answers to one
    // question. The questionnaire's own bias fieldset stays hidden (see the SCSS) so the Setup
    // shows exactly ONE directional-bias control, here, where the ruling puts it.

    readonly biasEnabledButtons = [
        {id: 'no', text: 'No'},
        {id: 'yes', text: 'Yes'}
    ];

    readonly biasDirectionButtons = [
        {id: 'up', text: 'Up'},
        {id: 'down', text: 'Down'},
        {id: 'sideways', text: 'Sideways'},
        {id: 'auto', text: 'Auto'}
    ];

    get biasEnabled(): boolean {
        return !!(this.setup.dates && this.setup.dates.bias && this.setup.dates.bias.enabled);
    }

    get biasEnabledKeys(): string[] {
        return [this.biasEnabled ? 'yes' : 'no'];
    }

    get biasDirectionKeys(): string[] {
        const bias = this.setup.dates ? this.setup.dates.bias : null;
        return (bias && bias.direction) ? [bias.direction] : [];
    }

    @DetectMethodChanges()
    onBiasEnabledClick(e: any): void {
        if (!e || !e.itemData || !this.setup.dates) {
            return;
        }
        const enabled = e.itemData.id === 'yes';
        this.setup.dates.bias = {
            enabled: enabled,
            // Switching it off drops the direction: a direction with the bias off is a value the
            // trader cannot see, and it is the kind of leftover that later reads as a setting.
            direction: enabled ? this.setup.dates.bias.direction : null
        };
        // Inert, but highlighted like every other field (owner ruling 2026-09-15: "for consistency").
        this.refreshChangedKeys();
    }

    @DetectMethodChanges()
    onBiasDirectionClick(e: any): void {
        if (!e || !e.itemData || !this.setup.dates || !this.biasEnabled) {
            return;
        }
        this.setup.dates.bias = {enabled: true, direction: e.itemData.id};
        this.refreshChangedKeys();
    }

    // ---- S5-S9, the two sides ---------------------------------------------------------------

    @DetectMethodChanges()
    onSideChanged(): void {
        // The side components mutate the working object in place; this exists to wake the OnPush
        // parent so the Apply gate, the messages and the changed-since-run cues re-evaluate.
        this.backendProblem = null;
        this.refreshChangedKeys();
    }

    sideMessages(side: 'calls' | 'puts'): string[] {
        return this.messagesAt(side === 'calls' ? 'callsCoverage' : 'putsCoverage');
    }

    // ---- S11, Apply and Reset ---------------------------------------------------------------

    @DetectMethodChanges()
    onReset(): void {
        this.clearEverything();
    }

    // A PORTFOLIO CHANGE IS A RESET (owner ruling 2026-09-15: "change of portfolio must reset all
    // fields and clear cards, just as the Reset button"). The same clearing as the button, then
    // the new portfolio takes the place the old one left: its templates, ladder, dates and zone
    // are loaded onto a blank Setup. Nothing of the previous portfolio's run or inputs survives.
    @DetectMethodChanges()
    private resetForPortfolioChange(portfolio: ApgPortfolio): void {
        this.clearEverything();
        // The box keeps showing the chosen portfolio while its reads are in flight; the ids and
        // the emit to the Details column follow in applyPortfolioSelection.
        this.selectedPortfolio = portfolio;
    }

    // The one definition of "back to where it opened" — the Reset button and a portfolio change.
    private clearEverything(): void {
        this._selectionGeneration++;
        this.clearOverrideHistory();
        this._selectedTemplateSettings = null;
        this._templateChangedKeys = {};
        this.templateUnderlying = null;
        this.templateBusy = false;
        this._allDatesOverride = null;
        this._templateDates = defaultMavenTemplateDates();
        this._zoneReadFor = null;
        this._portfolioZoneReadFor = null;
        // Reset clears live state and results. Explicit settings templates and last-used pointers
        // remain saved; no automatic portfolio reload is triggered by this gesture.
        this.setup = emptyMavenSetup(freshDates(this._orionSettings.mavenWeekdayExclusions));
        this.selectedPortfolio = null;
        this.templates = [];
        this.defaultQty = null;
        this.cards = [];
        this.currentRun = null;
        this.strategyType = null;
        this.backendProblem = null;
        // The zone line returns to N/A (no portfolio, no zone) and forgets the last run's zone.
        this.currentZone = null;
        this.lastRunZone = null;
        // ...and the Portfolio Zone with it (no portfolio, no shorts).
        this.portfolioZone = NO_PORTFOLIO_ZONE;
        // CR-20260916-003: the Close list's report goes too (the Setup above is already Ignore).
        this.closeBook = null;
        // ...and the last run's Setup: nothing is "changed since the run" once there is no run.
        this.lastRunSetup = null;
        this.refreshChangedKeys();
        // CR-20260916-004: nor is the data — the stripe goes with the run.
        this._runData = null;
        this._runDataLive = {};
        this.runDataChanged = null;

        // Everything DERIVED FROM THE PORTFOLIO goes with it: the strike ladder, the expiration
        // list and the Start anchor. Left in place they fed the pickers a ladder and a calendar
        // belonging to a portfolio no longer chosen.
        this.targetInstrument = null;
        this.strikeExpiration = null;
        this.availableExpirations = [];
        this.expirationsLoading = false;
        this._anchorKey = null;
        // CR-20260915-001: the override block's symbol, descriptors and date list go with the
        // portfolio too. The map itself was replaced by emptyMavenSetup above.
        this.underlying = null;
        this.expirationDescriptors = [];
        this._overrideDateKeys = [];

        // The date questionnaire holds its own inputs; this is the only way they clear. Its reset
        // emits, and onDatesChanged writes the emitted empty inputs back into `setup.dates` — so
        // what the trader sees and what Apply would send are one thing.
        if (this._datesQuestionnaire) {
            this._datesQuestionnaire.resetInputs();
        }

        // ⚠ THE OTHER TWO COLUMNS RESET WITH THIS ONE (vrequest 2026-09-13, item 16). Before this,
        // the fields above were cleared and nothing was emitted, so the panel kept its `delivery`
        // and the Results and Details columns stayed full beside an empty Setup. The settled-null
        // path is the same one a fresh Apply takes to blank them.
        this.stagesChange.emit([]);
        this.runSettled.emit(null);
        this.portfolioChange.emit(null);
    }

    async onApply(): Promise<void> {
        if (!this.canRun) {
            return;
        }

        // A SECOND APPLY SUPERSEDES the run in flight (ruling R-D). There is no stop control and
        // no cancel command: the new run goes out, and the backend pushes 'superseded' to the
        // displaced one exactly once. The displaced run's own settle path discards it — a late
        // result never reaches the cards.
        const run: MavenRun = {
            id: '',
            startedAt: Date.now(),
            endedAt: null,
            outcome: 'pending',
            stages: [],
            failureDetail: null,
            existingHedges: this.setup.existingHedges,
            // CR-20260916-003: the selection as SENT, its own copy, for the same reason.
            closeSelection: {
                expirations: (this.setup.closeSelection ? this.setup.closeSelection.expirations : []).slice(),
                hedgeIds: (this.setup.closeSelection ? this.setup.closeSelection.hedgeIds : []).slice()
            }
        };

        // The zone this run goes out under. canRun above guarantees it is not N/A. From here the
        // Current Zone line compares against it and turns stale if the footer moves the zone.
        this.lastRunZone = this.currentZone;

        // THE BASELINE (owner vrequest 2026-09-15): the Setup as sent, deep-copied, so every edit
        // from here on can be compared against it and painted yellow. Taken BEFORE the wire, so
        // what was sent and what is compared against are one object. Last-run cues clear;
        // selected-template differences remain in the combined map.
        this.lastRunSetup = cloneMavenSetup(this.setup);
        this.refreshChangedKeys();

        // CR-20260916-004: the DATA baseline too — the saved positions and hedge book as they are
        // at this moment, for the portfolio owner. Awaited before the wire so that any echo that
        // lands after the order went out is compared against what the order was priced on.
        await this.snapshotRunData(this.selectedPortfolio);

        this.beginRun(run);

        const result = await this._runSkill.run(
            {panelInstanceId: this.panelInstanceId, setup: this.setup, defaultQty: this.defaultQty || 0},
            event => this.onStage(run, event)
        );

        // ⚠ EVERY OUTCOME IS HANDLED, and the compiler is what guarantees it (ruling L46): the
        // result is a discriminated union whose `cards` exist only on the succeeded arm, so there
        // is no way to draw a column without first saying which outcome this was. A terminalFailed
        // can never quietly render as an empty panel.
        switch (result.outcome) {
            case 'succeeded':
                // ONE reveal, in the run's order. The backend's default quantity comes with it,
                // because that is the base every normalized figure on every card was divided by.
                this.defaultQty = result.defaultQty;
                this.strategyType = result.strategyType;
                this.endRun(run, 'succeeded', null, result.cards, result.expectedRangeNotes);
                return;

            case 'empty':
                // The single exception to R-C's no-messages rule: one sentence, because a blank
                // panel reads as a failure. Plus, since CR-20260914-001, the expected-range lines.
                this.endRun(run, 'empty', result.sentence, [], result.expectedRangeNotes);
                return;

            case 'superseded':
                // A newer run already owns the column. Settle this one's own record and draw
                // NOTHING — the displaced run must not blank what replaced it.
                run.outcome = 'superseded';
                run.endedAt = Date.now();
                return;

            case 'preSendBlocked': {
                this.endRun(run, 'preSendBlocked', result.detail, []);
                // The wire carries a CODE; the wording lives here and is rendered beside the
                // control that caused it — exactly where a local refusal appears, so the trader
                // cannot tell which tier caught him.
                const refusal = resolveMavenRefusal(result.code, result.detail);
                this.setBackendProblem(refusal.placement, refusal.message, refusal.isDefect);
                return;
            }

            case 'terminalFailed': {
                this.endRun(run, 'terminalFailed', result.detail, []);
                const failure = resolveMavenRefusal(null, result.detail);
                this.setBackendProblem(failure.placement, failure.message, failure.isDefect);
                return;
            }
        }
    }

    @DetectMethodChanges()
    private beginRun(run: MavenRun): void {
        this.isRunning = true;
        this.currentRun = run;
        this.cards = [];
        // The previous run's portfolio fact must not survive into this one: the trader may have
        // changed portfolio, and a stale "double" would open both pop-ups on the wrong footing.
        this.strategyType = null;
        this.backendProblem = null;

        // The column clears and shows the rail. A second Apply lands here again, which is what
        // supersede looks like from the outside: no stop control, just a fresh run.
        this.runningChange.emit(true);
        this.stagesChange.emit([]);
        this.runSettled.emit(null);
    }

    @DetectMethodChanges()
    private onStage(run: MavenRun, event: { stage: string; detail: string | null }): void {
        if (this.currentRun !== run) {
            return;   // a displaced run's stage line must not move the panel
        }
        run.stages.push({stage: event.stage, detail: event.detail, at: Date.now()});

        // Constants only. `detail` is deliberately NOT emitted: it may carry a count, and a figure
        // on the rail is something the trader can subtract the visible cards from (ruling L28).
        this.stagesChange.emit(run.stages.map(x => x.stage));
    }

    @DetectMethodChanges()
    private endRun(run: MavenRun, outcome: string, failureDetail: string | null, cards: MavenCardDto[],
                   expectedRangeNotes: string[] = []): void {
        run.outcome = outcome as any;
        run.endedAt = Date.now();
        run.failureDetail = failureDetail;
        if (this.currentRun === run) {
            this.isRunning = false;
            // ONE reveal: the cards arrive together, in the run's order, and a tick never
            // re-orders them.
            this.cards = cards || [];

            // The identity check above is the supersede guard, and the emit belongs INSIDE it: a
            // displaced run must not push its late result at the column that replaced it.
            this.runningChange.emit(false);
            this.runSettled.emit({
                outcome: outcome,
                failureDetail: failureDetail,
                cards: this.cards,
                defaultQty: this.defaultQty || 0,
                strategyType: this.strategyType,
                existingHedges: run.existingHedges,
                expectedRangeNotes: expectedRangeNotes || []
            });
        }
    }

    @DetectMethodChanges()
    private setBackendProblem(placement: MavenRefusalPlacement, message: string, isDefect: boolean): void {
        // CR-20260916-001 (owner ruling 4): a 'toast' refusal is a warning toast and NOTHING in
        // place — `backendProblem` stays null so messagesAt() paints no red line anywhere. The
        // run was refused pre-send, so the columns already stayed as they were (endRun above).
        if (placement === 'toast') {
            this._toastr.warning(message);
            return;
        }
        this.backendProblem = {placement, message, isDefect};
    }
}

/**
 * What a settled run hands to the other two columns.
 *
 * `cards` is the run's order, best credit per day first, and it is taken as given — the Results
 * column never re-ranks it. `defaultQty` travels with it because it is the base every normalized
 * figure on every card was divided by, and a card without it cannot state its own price.
 */
export interface MavenRunSettled {
    outcome: string;
    failureDetail: string | null;
    cards: MavenCardDto[];
    defaultQty: number;
    /** The Existing Hedges choice the run was sent with — decides the card's breakdown shape. */
    existingHedges: MavenExistingHedges;
    /** "single" | "double" on a succeeded run; null otherwise. Never defaulted (ruling L39). */
    strategyType: string | null;
    /** CR-20260914-001, the scoped exception to R-C: the expected-range lines, as delivered. */
    expectedRangeNotes: string[];
}

// The date block as it starts. The weekday exclusions come from Maven's OWN "Weekdays to exclude"
// shell setting (CR-20260915-002; NONE by default - the stakeholders ruled Maven must not exclude
// Monday, so the APG's Monday preset no longer applies here). Stated explicitly rather than left
// empty because "no exclusions" and "the setting's exclusions" are different starting points and
// only one of them is the ruled one.
function freshDates(defaultWeekdayExclusions: Weekday[]): RollPositionsInputs {
    return {
        date: {kind: 'range', start: null, end: null},
        weekdayExclusions: (defaultWeekdayExclusions || []).slice(),
        specificDateExclusions: [],
        weekdayOverrides: [],
        bias: {enabled: false, direction: null}
    };
}
